// The day-array maths of removing a program instance, out of the component
// (US-064). Pure: they read `days` and return what to write back.

// A program that needs an option from the previous program (a transition
// extra) cannot leave without warning the user which options go with it.
export function getRequiredRemovalOptions({
  dayIndex,
  experience,
  days,
  experiencesMap,
}: {
  dayIndex: number;
  experience: any;
  days: any[];
  experiencesMap: Record<string, any>;
}) {
  const startIndex = dayIndex - ((experience?.dayIndex ?? 1) - 1);

  let prevProgram = null;
  for (let i = startIndex - 1; i >= 0; i--) {
    const expId = days[i]?.experienceId;
    if (expId && experiencesMap[expId]) {
      prevProgram = experiencesMap[expId];
      break;
    }
  }

  const requiredOptions = prevProgram
    ? Array.from(
        days.reduce((acc: Set<string>, day: any) => {
          (day.selectedExtras ?? []).forEach((extra: any) => {
            if (extra.transitionalExperienceId === experience?.id) {
              acc.add(extra.name);
            }
          });
          return acc;
        }, new Set<string>()),
      )
    : [];

  return { prevProgram, requiredOptions: requiredOptions as string[] };
}

function removeTransitionExtras(
  selectedExtras: any[],
  removedExperienceId: string,
  removedParentExtraIds: Set<string>,
) {
  if (!removedExperienceId) {
    return selectedExtras;
  }

  const transitionExtrasToRemove = selectedExtras.filter(
    (extra) => extra?.transitionalExperienceId === removedExperienceId,
  );
  const allParentIdsToRemove = new Set([
    ...removedParentExtraIds,
    ...transitionExtrasToRemove.map((e) => e.id),
  ]);

  // Filter out extras that are transitional or children of removed parents
  return selectedExtras.filter(
    (extra) =>
      extra?.transitionalExperienceId !== removedExperienceId &&
      !allParentIdsToRemove.has(extra?.parentExtraId),
  );
}

// Inaccessible experiences are returned with minimal data and may not include
// `durationDays`. Infer the duration from the contiguous days occupied by the
// same experience so removing one removes the whole multi-day experience.
function getRemovalDuration(days: any[], dayIndex: number, experience: any) {
  const durationFromDays = days
    .slice(dayIndex)
    .findIndex((day) => day?.experienceId !== experience?.id);
  const inferredDuration =
    durationFromDays === -1 ? days.length - dayIndex : durationFromDays;

  return experience?.durationDays ?? Math.max(inferredDuration, 1);
}

function clearExperienceDay(day: any) {
  const isIncludedHotel = day.hotelIsIncluded;

  return {
    ...day,
    experienceId: null,
    hotelId: !isIncludedHotel ? (day.hotelId ?? null) : null,
    experienceDayIndex: null,
    experiencePricing: null,
    tripPrice: null,
    hotelPricing: !isIncludedHotel ? (day.hotelPricing ?? null) : null,
    hotelBookedOnMyOwn: false,
    selectedExtras: [],
    priceLoading: false,
    skipTransitionOption: false,
  };
}

// The days once the instance starting at `dayIndex` is gone: its range is
// cleared and every transition extra it brought (or that hung off one) with it.
export function removeExperienceFromDays({
  days,
  dayIndex,
  experience,
}: {
  days: any[];
  dayIndex: number;
  experience: any;
}) {
  const duration = getRemovalDuration(days, dayIndex, experience);
  const newDays = days.map((day, index) => {
    const inRange =
      (index >= dayIndex && index < dayIndex + duration) ||
      (index === dayIndex && duration === 0);

    return inRange ? clearExperienceDay(day) : day;
  });

  const removedParentExtraIds = new Set<string>();
  newDays.forEach((day) => {
    if (Array.isArray(day.selectedExtras)) {
      day.selectedExtras.forEach((extra: any) => {
        if (extra?.transitionalExperienceId === experience?.id) {
          removedParentExtraIds.add(extra.id);
        }
      });
    }
  });

  const cleanedDays = newDays.map((day) =>
    Array.isArray(day.selectedExtras) && day.selectedExtras.length > 0
      ? {
          ...day,
          selectedExtras: removeTransitionExtras(
            day.selectedExtras,
            experience?.id,
            removedParentExtraIds,
          ),
        }
      : day,
  );

  return cleanedDays.filter(
    (day) => day || dayIndex < cleanedDays.findIndex((d) => d?.experienceId),
  );
}
